import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, switchMap, tap, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

/**
 * In-app announcements — "What's New" and other product news.
 *
 * See `tasks/announcements.md` (Phase E1). Shaped after `notification.service.ts`, which solves the
 * same problem — an HTTP read driven off the selected group, kept current by a socket event.
 *
 * ── TWO SURFACES, ONE REQUEST ───────────────────────────────────────────────────────
 *
 * `GET /api/announcements` returns both partitions at once. The client needs both on login, and
 * splitting them would double the round trips for no benefit — the server has already loaded the
 * rows to decide which partition each belongs in.
 *
 * A third partition, `strip`, used to carry live incidents to a marquee in the header. It was
 * removed along with the `incident` and `maintenance` types (`tasks/system-status.md` §7):
 * subsystem health is a STATE rather than an event, and now lives behind `SystemStatusService`
 * and the status dot.
 *
 * `dialog$`   what may interrupt, already capped and ordered by the server (critical first).
 * `archive$`  the megaphone menu — everything that ever reached this user, expired and resolved
 *             included.
 *
 * ── THE SOCKET EVENT CARRIES NO PAYLOAD, DELIBERATELY ───────────────────────────────
 *
 * `announcementsChanged` is a bare signal. The client re-fetches and the SERVER re-applies the
 * audience for this user, so a role-targeted announcement is never broadcast to people outside its
 * audience — which a payload-bearing event on a shared namespace would do by construction.
 */

export type AnnouncementType = 'release' | 'info' | 'critical';

export interface Announcement {
    id: string;
    title: string;
    /** Server-rendered and tag-stripped. Bind with a plain `[innerHTML]` — see below. */
    bodyHtml: string;
    type: AnnouncementType;
    revision: number;
    dismissible: boolean;
    requiresAcknowledgement: boolean;
    ctaLabel?: string | null;
    ctaUrl?: string | null;
    publishedAt: string;
    expiresAt?: string | null;
    resolved: boolean;
    live: boolean;
    unread: boolean;
    receiptState: 'seen' | 'snoozed' | 'dismissed' | 'acknowledged' | null;
}

interface AnnouncementsResponse {
    success: boolean;
    dialog: Announcement[];
    archive: Announcement[];
    unreadCount: number;
}

const EMPTY_STATE: AnnouncementsResponse = {
    success: true, dialog: [], archive: [], unreadCount: 0,
};

@Injectable({
    providedIn: 'root',
})
export class AnnouncementService {

    private dialogSubject = new BehaviorSubject<Announcement[]>([]);
    dialog$: Observable<Announcement[]> = this.dialogSubject.asObservable();

    private archiveSubject = new BehaviorSubject<Announcement[]>([]);
    archive$: Observable<Announcement[]> = this.archiveSubject.asObservable();

    private unreadSubject = new BehaviorSubject<number>(0);
    unreadCount$: Observable<number> = this.unreadSubject.asObservable();

    private currentGroupId: string | null = null;

    constructor(
        private http: HttpClient,
        private socketService: SocketService,
        private groupService: GroupService,
    ) {
        this.groupService.selectedGroup$.pipe(
            switchMap((selectedGroup) => {
                this.currentGroupId = selectedGroup ? selectedGroup.id : null;
                return this.load();
            }),
        ).subscribe();

        // Payload-free by design — see the header.
        this.socketService.listen('announcementsChanged', 'announcement-service')
            .subscribe(() => this.load().subscribe());
    }

    private getAuthHeaders(): HttpHeaders {
        const token = localStorage.getItem('token');
        return new HttpHeaders().set('Authorization', `Bearer ${token}`);
    }

    /**
     * Re-reads both surfaces.
     *
     * Failures are swallowed into the empty state rather than propagated. An announcement is
     * informational by definition, so a failed fetch must never surface an error to a clinician or
     * break the login it happens to run alongside; the next fetch picks it up.
     */
    load(): Observable<AnnouncementsResponse> {
        const query = this.currentGroupId ? `?groupId=${encodeURIComponent(this.currentGroupId)}` : '';

        return this.http.get<AnnouncementsResponse>(
            `${environment.API_URL}/api/announcements${query}`,
            { headers: this.getAuthHeaders() },
        ).pipe(
            catchError(() => of(EMPTY_STATE)),
            tap((result) => {
                this.dialogSubject.next(result.dialog || []);
                this.archiveSubject.next(result.archive || []);
                this.unreadSubject.next(result.unreadCount || 0);
            }),
        );
    }

    /** Shown but not answered. Counts toward the passive-view cap the server enforces. */
    markSeen(id: string): Observable<any> {
        return this.post(id, 'seen');
    }

    /** "Remind me later". The server clamps `hours` to 1-168 and defaults it to 24. */
    snooze(id: string, hours?: number): Observable<any> {
        return this.post(id, 'snooze', { hours });
    }

    /** Permanent. Refused with a 409 when the announcement is not dismissible. */
    dismiss(id: string): Observable<any> {
        return this.post(id, 'dismiss');
    }

    /** `critical` only. Writes the audit timestamp. */
    acknowledge(id: string): Observable<any> {
        return this.post(id, 'acknowledge');
    }

    private post(id: string, action: string, body: any = {}): Observable<any> {
        const payload = { ...body, groupId: this.currentGroupId };
        return this.http.post(
            `${environment.API_URL}/api/announcements/${id}/${action}`,
            payload,
            { headers: this.getAuthHeaders() },
        ).pipe(
            // Re-read rather than mutating local state: the server owns the partitioning rules
            // (passive-view caps, snooze windows, revision comparisons), and a client that tried to
            // reproduce them would drift out of step with them.
            switchMap(() => this.load()),
            catchError(() => EMPTY),
        );
    }
}
